import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ChevronRight,
  Sparkles,
  Target,
  CheckCircle2,
  PlayCircle,
  Lock,
  Award,
  Brain,
  Compass,
  TrendingUp,
} from 'lucide-react';
import useSafeBackNavigation from '../hooks/useSafeBackNavigation';
// 🔄 Import événements pédagogiques pour refresh automatique
import { subscribeToEvaluation } from '../utils/pedagogicalEvents';

import { getBackendUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/* ============================================================
   🎨 DESIGN TOKENS — cohérents avec Iter304-307
   ============================================================ */
const TOKENS = {
  ink: '#1C1147',
  inkSoft: '#3A2E6E',
  ivory: '#FAFAF8',
  ivoryDeep: '#F3EFE3',
  border: '#E8E4D9',
  goldDeep: '#B7901F',
  gold: '#D4AF37',
  goldLight: '#F3D060',
  goldGlow: 'rgba(212, 175, 55, 0.45)',
  validated: '#48825A',
  inProgress: '#D4AF37',
  upcoming: '#A89F8C',
};

/* ============================================================
   🧮 PROGRESS HELPERS
   ============================================================ */
// Modèle pédagogique : 2 objectifs × 5 critères = 10 critères par module
const TOTAL_CRITERIA = 10;
const TOTAL_OBJECTIVES = 2;

const getModuleStatus = (progress) => {
  if (!progress) return 'upcoming';
  if (progress.validated) return 'validated';
  if (progress.progress > 0) return 'in_progress';
  return 'upcoming';
};

const getCriteriaCount = (progress) => {
  if (!progress) return 0;
  if (progress.validated) return TOTAL_CRITERIA;
  return Math.round(((progress.progress || 0) / 100) * TOTAL_CRITERIA);
};

const getObjectivesCount = (progress) => {
  if (!progress?.objectivesValidated) return 0;
  const [done] = String(progress.objectivesValidated).split('/');
  return parseInt(done, 10) || 0;
};

const STATUS_CONFIG = {
  validated: {
    label: 'Validé',
    dot: TOKENS.validated,
    chipBg: 'rgba(72, 130, 90, 0.10)',
    chipText: TOKENS.validated,
    chipBorder: 'rgba(72, 130, 90, 0.35)',
    cta: 'Revoir ce module',
    icon: Award,
  },
  in_progress: {
    label: 'En cours',
    dot: TOKENS.gold,
    chipBg: 'rgba(212, 175, 55, 0.12)',
    chipText: TOKENS.goldDeep,
    chipBorder: 'rgba(212, 175, 55, 0.45)',
    cta: 'Continuer ce module',
    icon: PlayCircle,
  },
  upcoming: {
    label: 'À démarrer',
    dot: TOKENS.upcoming,
    chipBg: 'rgba(28, 17, 71, 0.05)',
    chipText: TOKENS.inkSoft,
    chipBorder: 'rgba(28, 17, 71, 0.15)',
    cta: 'Commencer ce module',
    icon: Compass,
  },
};

/* ============================================================
   🏷️ DIFFICULTY BADGE
   ============================================================ */
const DIFFICULTY_CONFIG = {
  Débutant: { label: 'Niveau · Fondations', accent: '#48825A', bg: 'rgba(72,130,90,0.08)' },
  Intermédiaire: { label: 'Niveau · Pratique', accent: '#B7901F', bg: 'rgba(212,175,55,0.10)' },
  Avancé: { label: 'Niveau · Maîtrise', accent: '#7A4FCF', bg: 'rgba(122,79,207,0.10)' },
};
const getDifficultyConfig = (d) => DIFFICULTY_CONFIG[d] || DIFFICULTY_CONFIG['Débutant'];

/* ============================================================
   🧩 ATOM — Status Pill
   ============================================================ */
const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide"
      style={{
        background: cfg.chipBg,
        color: cfg.chipText,
        border: `1px solid ${cfg.chipBorder}`,
      }}
      data-testid={`module-status-${status}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: cfg.dot, boxShadow: `0 0 6px ${cfg.dot}` }}
      />
      {cfg.label}
    </span>
  );
};

/* ============================================================
   🧩 ATOM — Criteria Dots (10 dots, X validés)
   ============================================================ */
const CriteriaDots = ({ count, status }) => {
  return (
    <div className="flex items-center gap-1" data-testid="module-criteria-dots">
      {Array.from({ length: TOTAL_CRITERIA }).map((_, i) => {
        const filled = i < count;
        const color =
          status === 'validated'
            ? TOKENS.validated
            : filled
              ? TOKENS.gold
              : 'rgba(28,17,71,0.12)';
        return (
          <span
            key={i}
            className="rounded-full transition-all"
            style={{
              width: 7,
              height: 7,
              background: color,
              boxShadow: filled ? `0 0 4px ${color}` : 'none',
            }}
          />
        );
      })}
    </div>
  );
};

/* ============================================================
   🧩 PREMIUM MODULE CARD
   ============================================================ */
const ModuleCard = ({ module, index, progress, onClick }) => {
  const status = getModuleStatus(progress);
  const statusCfg = STATUS_CONFIG[status];
  const StatusIcon = statusCfg.icon;
  const diffCfg = getDifficultyConfig(module.difficulty);
  const criteriaCount = getCriteriaCount(progress);
  const objectivesCount = getObjectivesCount(progress);
  const pct = progress?.validated ? 100 : Math.round(progress?.progress || 0);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={`module-card-${module.id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="group relative text-left w-full h-full rounded-[20px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        background: '#FFFFFF',
        border: `1px solid ${TOKENS.border}`,
        boxShadow:
          '0 1px 0 rgba(28,17,71,0.04), 0 12px 30px -22px rgba(28,17,71,0.25)',
        transition: 'box-shadow 320ms ease, border-color 320ms ease, transform 320ms ease',
      }}
    >
      {/* hover glow overlay */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-[20px] opacity-0 group-hover:opacity-100 pointer-events-none"
        style={{
          background:
            'radial-gradient(120% 80% at 0% 0%, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0) 55%)',
          transition: 'opacity 320ms ease',
        }}
      />

      {/* hover border + lift */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-[20px] pointer-events-none"
        style={{
          boxShadow:
            'inset 0 0 0 1px rgba(212,175,55,0)',
          transition: 'box-shadow 320ms ease',
        }}
      />

      {/* Top accent bar — couleur niveau */}
      <span
        aria-hidden
        className="absolute top-0 left-6 right-6 h-[2px] rounded-b-full"
        style={{
          background: `linear-gradient(90deg, ${diffCfg.accent}00 0%, ${diffCfg.accent} 50%, ${diffCfg.accent}00 100%)`,
          opacity: 0.55,
        }}
      />

      <div className="p-5 sm:p-6 flex flex-col h-full">
        {/* === Header : numéro module + status pill === */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center font-serif"
              style={{
                background:
                  'linear-gradient(135deg, #FFFCEC 0%, #FFF4D2 100%)',
                border: `1px solid ${TOKENS.border}`,
                color: TOKENS.goldDeep,
                fontSize: '1.05rem',
                fontWeight: 700,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
              }}
            >
              {String(module.order).padStart(2, '0')}
            </div>
            <div className="flex flex-col gap-1">
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: diffCfg.accent }}
              >
                {diffCfg.label}
              </span>
              <StatusPill status={status} />
            </div>
          </div>

          <ChevronRight
            className="w-5 h-5 mt-1 transition-transform group-hover:translate-x-1"
            style={{ color: TOKENS.gold }}
          />
        </div>

        {/* === Titre serif === */}
        <h3
          className="font-serif leading-snug mb-2"
          style={{
            color: TOKENS.ink,
            fontSize: '1.15rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {module.title}
        </h3>

        {/* === Objectif court === */}
        {module.objective && (
          <div className="flex items-start gap-2 mb-4">
            <Target
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: TOKENS.goldDeep }}
            />
            <p
              className="text-[13px] leading-relaxed line-clamp-2"
              style={{ color: TOKENS.inkSoft }}
            >
              {module.objective}
            </p>
          </div>
        )}

        {/* === Bloc Preuves : 2 objectifs / 10 critères === */}
        <div
          className="rounded-xl px-4 py-3 mb-4"
          style={{
            background:
              'linear-gradient(180deg, #FBF8EE 0%, #FFFFFF 100%)',
            border: `1px solid ${TOKENS.border}`,
          }}
          data-testid={`module-evidence-${module.id}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{ color: TOKENS.inkSoft }}
            >
              Preuves à démontrer
            </span>
            <span
              className="text-[11px] font-semibold tabular-nums"
              style={{ color: TOKENS.goldDeep }}
            >
              {criteriaCount}/{TOTAL_CRITERIA} critères
            </span>
          </div>

          {/* Dots ligne critères */}
          <CriteriaDots count={criteriaCount} status={status} />

          {/* Barre objectifs */}
          <div className="flex items-center gap-3 mt-3">
            <div className="flex items-center gap-1.5">
              <div className="flex gap-1">
                {Array.from({ length: TOTAL_OBJECTIVES }).map((_, i) => (
                  <span
                    key={i}
                    className="block rounded-[3px]"
                    style={{
                      width: 14,
                      height: 4,
                      background:
                        i < objectivesCount
                          ? `linear-gradient(90deg, ${TOKENS.goldDeep}, ${TOKENS.gold})`
                          : 'rgba(28,17,71,0.08)',
                    }}
                  />
                ))}
              </div>
              <span
                className="text-[11px] font-medium"
                style={{ color: TOKENS.inkSoft }}
              >
                {objectivesCount}/{TOTAL_OBJECTIVES} objectifs
              </span>
            </div>
            <div
              className="ml-auto text-[11px] font-semibold tabular-nums"
              style={{ color: status === 'validated' ? TOKENS.validated : TOKENS.ink }}
            >
              {pct}%
            </div>
          </div>

          {/* Barre progression fine */}
          <div
            className="mt-2 h-[3px] rounded-full overflow-hidden"
            style={{ background: 'rgba(28,17,71,0.06)' }}
          >
            <motion.div
              className="h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(pct, 100)}%` }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              style={{
                background:
                  status === 'validated'
                    ? `linear-gradient(90deg, ${TOKENS.validated}, #6BA77E)`
                    : `linear-gradient(90deg, ${TOKENS.goldDeep}, ${TOKENS.goldLight})`,
              }}
            />
          </div>
        </div>

        {/* === CTA === */}
        <div className="mt-auto">
          <div
            className="flex items-center justify-between rounded-xl px-4 py-3 transition-all"
            style={{
              background:
                status === 'validated'
                  ? 'linear-gradient(135deg, #F5FAF6 0%, #FFFFFF 100%)'
                  : 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 100%)',
              border: `1px solid ${
                status === 'validated'
                  ? 'rgba(72,130,90,0.30)'
                  : 'rgba(212,175,55,0.40)'
              }`,
            }}
          >
            <span className="flex items-center gap-2">
              <StatusIcon
                className="w-4 h-4"
                style={{
                  color:
                    status === 'validated' ? TOKENS.validated : TOKENS.goldDeep,
                }}
              />
              <span
                className="text-[13px] font-semibold"
                style={{
                  color:
                    status === 'validated' ? TOKENS.validated : TOKENS.ink,
                }}
              >
                {statusCfg.cta}
              </span>
            </span>
            <ChevronRight
              className="w-4 h-4 transition-transform group-hover:translate-x-1"
              style={{
                color:
                  status === 'validated' ? TOKENS.validated : TOKENS.goldDeep,
              }}
            />
          </div>
        </div>
      </div>

      {/* CSS hover boost via :hover sur group */}
      <style>{`
        button[data-testid="module-card-${module.id}"]:hover {
          border-color: ${TOKENS.gold} !important;
          box-shadow:
            0 1px 0 rgba(212,175,55,0.20),
            0 22px 50px -28px rgba(28,17,71,0.35),
            0 0 0 1px rgba(212,175,55,0.18) !important;
        }
      `}</style>
    </motion.button>
  );
};

/* ============================================================
   🧩 STATS BAR — Synthèse pilier
   ============================================================ */
const PillarStatsBar = ({ stats }) => {
  const { total, validated, inProgress, upcoming, criteriaTotal, criteriaDone } = stats;
  const cells = [
    {
      label: 'Modules validés',
      value: `${validated}/${total}`,
      icon: Award,
      color: TOKENS.validated,
    },
    {
      label: 'En cours',
      value: inProgress,
      icon: PlayCircle,
      color: TOKENS.goldDeep,
    },
    {
      label: 'À démarrer',
      value: upcoming,
      icon: Compass,
      color: TOKENS.inkSoft,
    },
    {
      label: 'Preuves démontrées',
      value: `${criteriaDone}/${criteriaTotal}`,
      icon: TrendingUp,
      color: TOKENS.ink,
    },
  ];

  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3"
      data-testid="pillar-stats-bar"
    >
      {cells.map((c, i) => {
        const Icon = c.icon;
        return (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.06 }}
            className="rounded-2xl px-4 py-3 flex items-center gap-3"
            style={{
              background: 'rgba(255,255,255,0.72)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid ${TOKENS.border}`,
              boxShadow: '0 1px 0 rgba(28,17,71,0.03)',
            }}
          >
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  'linear-gradient(135deg, #FFFCEC 0%, #FFF4D2 100%)',
                border: `1px solid ${TOKENS.border}`,
              }}
            >
              <Icon className="w-4 h-4" style={{ color: c.color }} />
            </span>
            <div className="flex flex-col min-w-0 flex-1">
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.08em] leading-tight break-words"
                style={{ color: TOKENS.inkSoft, hyphens: 'auto' }}
              >
                {c.label}
              </span>
              <span
                className="font-serif tabular-nums"
                style={{
                  color: TOKENS.ink,
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {c.value}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

/* ============================================================
   🧩 LOADING STATE
   ============================================================ */
const LoadingState = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: TOKENS.ivory }}
  >
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center gap-4"
    >
      <motion.div
        className="w-12 h-12 rounded-full"
        style={{
          border: `3px solid ${TOKENS.border}`,
          borderTopColor: TOKENS.gold,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p
        className="font-serif text-sm"
        style={{ color: TOKENS.inkSoft, letterSpacing: '0.02em' }}
      >
        Préparation du pilier…
      </p>
    </motion.div>
  </div>
);

/* ============================================================
   🚀 MAIN PAGE
   ============================================================ */
const PillarModules = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pillar, setPillar] = useState(null);
  const [modules, setModules] = useState([]);
  const [modulesProgress, setModulesProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const handleBack = useSafeBackNavigation('/training');

  /* ------- Fetch progress ------- */
  const fetchProgress = useCallback(async () => {
    try {
      const timestamp = Date.now();
      const token =
        localStorage.getItem('token') || localStorage.getItem('access_token');
      const progressRes = await axios.get(
        `${API}/pedagogical/dashboard/summary?_t=${timestamp}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
              }
            : {},
        }
      );

      const progressMap = {};
      if (progressRes.data?.modules_summary) {
        progressRes.data.modules_summary.forEach((m) => {
          progressMap[m.module_id] = {
            progress: m.progress_percentage || 0,
            validated: m.validated || false,
            objectivesValidated: m.objectives_validated || '0/2',
          };
        });
      }
      setModulesProgress(progressMap);
    } catch (error) {
      console.warn('[PillarModules] Progression non disponible:', error.message);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const fetchData = async () => {
      try {
        const [pillarRes, modulesRes] = await Promise.all([
          axios.get(`${API}/pillars/${id}`),
          axios.get(`${API}/modules-v2?pillar_id=${id}`),
        ]);
        setPillar(pillarRes.data);
        setModules(modulesRes.data);
        fetchProgress();
      } catch (error) {
        toast.error('Erreur lors du chargement');
        navigate('/training');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user, navigate, fetchProgress]);

  useEffect(() => {
    const unsubscribe = subscribeToEvaluation(() => {
      fetchProgress();
    });
    return unsubscribe;
  }, [fetchProgress]);

  /* ------- Aggregations pilier ------- */
  const stats = useMemo(() => {
    const total = modules.length;
    let validated = 0;
    let inProgress = 0;
    let upcoming = 0;
    let criteriaDone = 0;
    modules.forEach((m) => {
      const p = modulesProgress[m.id];
      const status = getModuleStatus(p);
      if (status === 'validated') validated += 1;
      else if (status === 'in_progress') inProgress += 1;
      else upcoming += 1;
      criteriaDone += getCriteriaCount(p);
    });
    return {
      total,
      validated,
      inProgress,
      upcoming,
      criteriaDone,
      criteriaTotal: total * TOTAL_CRITERIA,
    };
  }, [modules, modulesProgress]);

  const pillarPct = useMemo(() => {
    if (!stats.criteriaTotal) return 0;
    return Math.round((stats.criteriaDone / stats.criteriaTotal) * 100);
  }, [stats]);

  /* ------- Next recommended module (1er non-validé) ------- */
  const nextModule = useMemo(() => {
    if (!modules.length) return null;
    const sorted = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
      sorted.find((m) => {
        const p = modulesProgress[m.id];
        return getModuleStatus(p) === 'in_progress';
      }) ||
      sorted.find((m) => getModuleStatus(modulesProgress[m.id]) === 'upcoming') ||
      sorted[0]
    );
  }, [modules, modulesProgress]);

  if (loading) return <LoadingState />;

  return (
    <div
      className="min-h-screen relative"
      style={{ background: TOKENS.ivory }}
      data-testid="pillar-modules-page"
    >
      {/* === BG ambient layers === */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        <div
          className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div
          className="absolute top-[40%] -left-40 w-[480px] h-[480px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(122,79,207,0.10) 0%, rgba(122,79,207,0) 70%)',
            filter: 'blur(50px)',
          }}
        />
      </div>

      {/* === TOP BAR (sticky, glass) === */}
      <div
        className="sticky top-0 z-40"
        style={{
          background: 'rgba(250,250,248,0.78)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: `1px solid ${TOKENS.border}`,
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            data-testid="pillar-back-button"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-[13px] font-medium transition-all"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${TOKENS.border}`,
              color: TOKENS.ink,
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.7), 0 6px 18px -10px rgba(28,17,71,0.18)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212,175,55,0.55)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = TOKENS.border;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour aux piliers</span>
            <span className="sm:hidden">Retour</span>
          </button>

          <div className="hidden md:flex items-center gap-2 text-[12px]">
            <span style={{ color: TOKENS.inkSoft }}>Mon parcours</span>
            <ChevronRight className="w-3 h-3" style={{ color: TOKENS.upcoming }} />
            <span
              className="font-medium truncate max-w-[280px]"
              style={{ color: TOKENS.ink }}
            >
              {pillar?.name || pillar?.title}
            </span>
          </div>

          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{
              background: 'rgba(212,175,55,0.08)',
              border: `1px solid ${TOKENS.border}`,
            }}
            data-testid="pillar-global-progress"
          >
            <Sparkles className="w-3.5 h-3.5" style={{ color: TOKENS.goldDeep }} />
            <span
              className="text-[12px] font-semibold tabular-nums"
              style={{ color: TOKENS.ink }}
            >
              {pillarPct}%
            </span>
          </div>
        </div>
      </div>

      {/* === IMMERSIVE HEADER === */}
      <header className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-8 sm:pb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            {/* kicker */}
            <div className="flex items-center gap-2 mb-4">
              <span
                className="w-8 h-px"
                style={{
                  background: `linear-gradient(90deg, ${TOKENS.gold}, transparent)`,
                }}
              />
              <span
                className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: TOKENS.goldDeep }}
                data-testid="pillar-kicker"
              >
                Pilier · Formation Certifiante
              </span>
            </div>

            {/* Titre serif gradient or */}
            <h1
              className="font-serif mb-3 sm:mb-4"
              style={{
                fontSize: 'clamp(1.85rem, 4.5vw, 3rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${TOKENS.ink} 0%, ${TOKENS.inkSoft} 60%, ${TOKENS.goldDeep} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
              data-testid="pillar-title"
            >
              {pillar?.name || pillar?.title}
            </h1>

            {pillar?.description && (
              <p
                className="text-[15px] sm:text-base leading-relaxed max-w-2xl"
                style={{ color: TOKENS.inkSoft }}
                data-testid="pillar-description"
              >
                {pillar.description}
              </p>
            )}

            {/* Méthode badge inline */}
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  border: `1px solid ${TOKENS.border}`,
                  color: TOKENS.ink,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Brain className="w-3.5 h-3.5" style={{ color: '#7A4FCF' }} />
                Owen analyse · challenge · valide
              </div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  border: `1px solid ${TOKENS.border}`,
                  color: TOKENS.ink,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Award className="w-3.5 h-3.5" style={{ color: TOKENS.goldDeep }} />
                {modules.length} modules · 2×5 critères
              </div>
            </div>
          </motion.div>

          {/* Stats bar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8"
          >
            <PillarStatsBar stats={stats} />
          </motion.div>
        </div>
      </header>

      {/* === MODULES GRID === */}
      <main className="container mx-auto px-4 sm:px-6 pb-16 sm:pb-24 relative z-10">
        <div className="flex items-end justify-between mb-5 sm:mb-6">
          <div>
            <h2
              className="font-serif"
              style={{
                color: TOKENS.ink,
                fontSize: '1.35rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              Les modules de ce pilier
            </h2>
            <p
              className="text-[13px] mt-1"
              style={{ color: TOKENS.inkSoft }}
            >
              Chaque module = 2 objectifs × 5 critères à démontrer face à Owen.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6"
          data-testid="pillar-modules-grid"
        >
          <AnimatePresence>
            {modules.map((module, index) => (
              <ModuleCard
                key={module.id}
                module={module}
                index={index}
                progress={modulesProgress[module.id]}
                onClick={() => navigate(`/module-v2/${module.id}`)}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* === FOOTER CTA === */}
        {nextModule && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 sm:mt-16 relative overflow-hidden rounded-3xl"
            style={{
              background:
                'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 55%, #F7F2E2 100%)',
              border: `1.5px solid rgba(212,175,55,0.40)`,
              boxShadow:
                '0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 60px -32px rgba(28,17,71,0.30)',
            }}
            data-testid="pillar-footer-cta"
          >
            <div
              aria-hidden
              className="absolute -top-20 -right-20 w-72 h-72 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(212,175,55,0.25) 0%, rgba(212,175,55,0) 70%)',
                filter: 'blur(20px)',
              }}
            />
            <div className="relative px-6 sm:px-10 py-8 sm:py-10 flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" style={{ color: TOKENS.goldDeep }} />
                  <span
                    className="text-[11px] font-semibold uppercase tracking-[0.16em]"
                    style={{ color: TOKENS.goldDeep }}
                  >
                    Recommandation Owen
                  </span>
                </div>
                <h3
                  className="font-serif mb-2"
                  style={{
                    color: TOKENS.ink,
                    fontSize: 'clamp(1.25rem, 2.5vw, 1.6rem)',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.15,
                  }}
                >
                  {pillarPct >= 100
                    ? 'Pilier validé. Prochain défi vous attend.'
                    : pillarPct > 0
                      ? 'Continuez sur votre lancée'
                      : 'Démarrez par le bon module'}
                </h3>
                <p
                  className="text-[14px] leading-relaxed"
                  style={{ color: TOKENS.inkSoft }}
                >
                  Module recommandé&nbsp;:{' '}
                  <strong style={{ color: TOKENS.ink }}>{nextModule.title}</strong>
                  {nextModule.objective ? ` — ${nextModule.objective}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/module-v2/${nextModule.id}`)}
                data-testid="pillar-cta-continue"
                className="group relative inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-[14px] flex-shrink-0 transition-all"
                style={{
                  background: `linear-gradient(135deg, ${TOKENS.goldDeep} 0%, ${TOKENS.gold} 50%, ${TOKENS.goldLight} 100%)`,
                  color: '#1C1147',
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.4) inset, 0 14px 32px -10px rgba(212,175,55,0.55)',
                  border: '1px solid rgba(255,255,255,0.4)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 1px 0 rgba(255,255,255,0.4) inset, 0 22px 44px -12px rgba(212,175,55,0.7)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 1px 0 rgba(255,255,255,0.4) inset, 0 14px 32px -10px rgba(212,175,55,0.55)';
                }}
              >
                <PlayCircle className="w-4 h-4" />
                <span>
                  {getModuleStatus(modulesProgress[nextModule.id]) === 'in_progress'
                    ? 'Continuer ce module'
                    : 'Démarrer ce module'}
                </span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </motion.section>
        )}

        {/* Empty state */}
        {!modules.length && (
          <div
            className="text-center py-20 rounded-2xl"
            style={{
              background: 'rgba(255,255,255,0.6)',
              border: `1px dashed ${TOKENS.border}`,
            }}
          >
            <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: TOKENS.upcoming }} />
            <p style={{ color: TOKENS.inkSoft }}>
              Aucun module disponible pour ce pilier pour le moment.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PillarModules;
